import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Layers,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  User,
  ShieldCheck
} from 'lucide-react';
import classNames from 'classnames';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/login');
  };

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/tenants', icon: Building2, label: 'Müşteriler' },
    { path: '/features', icon: Layers, label: 'Özellikler' },
    { path: '/users', icon: ShieldCheck, label: 'Yöneticiler' },
  ];

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Sidebar */}
      <aside
        className={classNames(
          "fixed inset-y-0 left-0 z-50 w-64 bg-secondary-900 text-white transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 shadow-2xl",
          { "-translate-x-full": !isSidebarOpen, "translate-x-0": isSidebarOpen }
        )}
      >
        <div className="h-full flex flex-col">
          {/* Logo Area */}
          <div className="h-16 flex items-center px-6 bg-secondary-950/50 border-b border-secondary-800">
            <Layers className="w-8 h-8 text-primary-400 mr-3" />
            <span className="text-xl font-bold tracking-tight text-white">Stokly<span className="text-primary-400">Panel</span></span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            <div className="text-xs font-semibold text-secondary-400 uppercase tracking-wider mb-4 px-2">Genel</div>
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  classNames(
                    "flex items-center px-4 py-3 rounded-xl transition-all duration-200 group text-sm font-medium",
                    {
                      "bg-primary-600 text-white shadow-lg shadow-primary-900/20": isActive,
                      "text-secondary-300 hover:bg-secondary-800 hover:text-white": !isActive
                    }
                  )
                }
              >
                <item.icon className="w-5 h-5 mr-3 transition-transform group-hover:scale-110" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* User Profile / Logout */}
          <div className="p-4 bg-secondary-950/30 border-t border-secondary-800">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-300">
                <User size={16} />
              </div>
              <div>
                <div className="text-sm font-medium text-white">Süper Admin</div>
                <div className="text-xs text-secondary-400">admin@stokly.com</div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex w-full items-center px-4 py-2 text-red-300 hover:bg-red-500/10 hover:text-red-200 rounded-lg transition-colors text-sm"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Çıkış Yap
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="h-16 glass z-40 flex items-center justify-between px-6 sticky top-0">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-lg text-secondary-500 hover:bg-secondary-100 lg:hidden focus:ring-2 focus:ring-primary-500"
          >
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Search Bar (Placeholder) */}
          <div className="hidden md:flex items-center w-96 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Firmalarda veya özelliklerde ara..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50/50 border border-gray-200 rounded-full text-sm focus:bg-white focus:border-primary-300 focus:ring-4 focus:ring-primary-500/10 transition-all outline-none"
            />
          </div>

          <div className="flex items-center gap-4">
            <button className="relative p-2 rounded-full text-secondary-500 hover:bg-secondary-100 transition-colors">
              <Bell size={20} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50/50 p-6 scroll-smooth">
          <div className="max-w-7xl mx-auto animate-in fade-in duration-500 slide-in-from-bottom-4">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;